import { create } from 'zustand';
import type { DIDTabId, DIDListFilters } from '../types';

interface DIDState {
  // Selected DID for detail modal
  selectedDID: string | null;
  isDetailModalOpen: boolean;
  activeTab: DIDTabId;

  // Disconnect confirmation modal (can be used independently of detail modal)
  disconnectDID: string | null;
  isDisconnectModalOpen: boolean;

  // Table state
  selectedDIDs: string[];
  searchQuery: string;
  serviceFilter: string;
  filters: DIDListFilters;
  currentPage: number;
  pageSize: number;

  // Stale DIDs filter
  staleFilterActive: boolean;
  staleDIDsList: string[];

  // Actions
  openDetailModal: (tn: string, tab?: DIDTabId) => void;
  closeDetailModal: () => void;
  setActiveTab: (tab: DIDTabId) => void;

  // Disconnect modal actions
  openDisconnectModal: (tn: string) => void;
  closeDisconnectModal: () => void;

  // Selection actions
  selectDID: (tn: string) => void;
  deselectDID: (tn: string) => void;
  selectAllDIDs: (tns: string[]) => void;
  clearSelection: () => void;

  // Filter actions
  setSearchQuery: (query: string) => void;
  setServiceFilter: (filter: string) => void;
  setFilters: (filters: DIDListFilters) => void;
  resetFilters: () => void;

  // Stale filter actions
  setStaleFilter: (active: boolean, staleDIDs: string[]) => void;
  clearStaleFilter: () => void;

  // Pagination actions
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

const initialFilters: DIDListFilters = {
  type: 'all',
};

export const useDIDStore = create<DIDState>((set) => ({
  // Initial state
  selectedDID: null,
  isDetailModalOpen: false,
  activeTab: 'failover',
  disconnectDID: null,
  isDisconnectModalOpen: false,
  selectedDIDs: [],
  searchQuery: '',
  serviceFilter: '',
  filters: initialFilters,
  currentPage: 1,
  pageSize: 10,
  staleFilterActive: false,
  staleDIDsList: [],

  // Modal actions
  openDetailModal: (tn, tab = 'failover') =>
    set({
      selectedDID: tn,
      isDetailModalOpen: true,
      activeTab: tab,
    }),

  closeDetailModal: () =>
    set({
      selectedDID: null,
      isDetailModalOpen: false,
    }),

  setActiveTab: (tab) => set({ activeTab: tab }),

  // Disconnect modal actions
  openDisconnectModal: (tn) =>
    set({
      disconnectDID: tn,
      isDisconnectModalOpen: true,
    }),

  closeDisconnectModal: () =>
    set({
      disconnectDID: null,
      isDisconnectModalOpen: false,
    }),

  // Selection actions
  selectDID: (tn) =>
    set((state) => ({
      selectedDIDs: state.selectedDIDs.includes(tn)
        ? state.selectedDIDs
        : [...state.selectedDIDs, tn],
    })),

  deselectDID: (tn) =>
    set((state) => ({
      selectedDIDs: state.selectedDIDs.filter((id) => id !== tn),
    })),

  selectAllDIDs: (tns) => set({ selectedDIDs: tns }),

  clearSelection: () => set({ selectedDIDs: [] }),

  // Filter actions
  setSearchQuery: (query) => set({ searchQuery: query, currentPage: 1 }),
  setServiceFilter: (filter) => set({ serviceFilter: filter, currentPage: 1 }),

  setFilters: (filters) =>
    set((state) => ({
      filters: { ...state.filters, ...filters },
      currentPage: 1,
    })),

  resetFilters: () =>
    set({
      filters: initialFilters,
      searchQuery: '',
      currentPage: 1,
      staleFilterActive: false,
      staleDIDsList: [],
    }),

  // Stale filter actions
  setStaleFilter: (active, staleDIDs) =>
    set({
      staleFilterActive: active,
      staleDIDsList: staleDIDs,
      currentPage: 1,
    }),

  clearStaleFilter: () =>
    set({
      staleFilterActive: false,
      staleDIDsList: [],
      currentPage: 1,
    }),

  // Pagination actions
  setCurrentPage: (page) => set({ currentPage: page }),

  setPageSize: (size) => set({ pageSize: size, currentPage: 1 }),
}));
